import { AiToolCategory, getAiToolCategory } from './getAiToolLabel';

type ToolStepLike = {
  id: string;
  toolName: string;
  running?: boolean;
};

export type GroupedToolStep<T extends ToolStepLike> = {
  // The first step's id — stable as later steps of the same category join the
  // group, so it is safe to use as the React key.
  key: string;
  category: AiToolCategory;
  count: number;
  // The most recent step in the group — carries its `running` state and is
  // used to resolve the label's toolName.
  step: T;
};

// The assistant interleaves tool families as it works (search, list, search
// again, read, search again), so one line per call reads as noise and repeats
// the same label several times down the strip. Fold every step of the same
// category into a single row with a repeat count, whether or not the calls
// were adjacent; the row keeps the position of the category's FIRST call so
// rows never reorder mid-turn, and tracks the LATEST call's running state so
// the spinner sits on whatever is actually in flight.
export default function groupToolStepsByCategory<T extends ToolStepLike>(
  steps: T[],
): GroupedToolStep<T>[] {
  const groups: GroupedToolStep<T>[] = [];
  const groupByCategory = new Map<AiToolCategory, GroupedToolStep<T>>();
  steps.forEach((step) => {
    const category = getAiToolCategory(step.toolName);
    const existing = groupByCategory.get(category);
    if (existing) {
      existing.count += 1;
      existing.step = step;
      return;
    }
    const group: GroupedToolStep<T> = {
      key: step.id,
      category,
      count: 1,
      step,
    };
    groupByCategory.set(category, group);
    groups.push(group);
  });
  return groups;
}
